import { Component } from '@theme/component';
import {
  fetchConfig,
  debounce,
  onAnimationEnd,
  prefersReducedMotion,
  resetShimmer,
  startViewTransition,
} from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import {
  createViewEventElement,
  CartErrorEvent,
  CartDiscountUpdateEvent,
  CartLinesUpdateEvent,
  CartNoteUpdateEvent,
  StandardEvents,
} from '@shopify/events';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * PERSONALISER BUNDLES
 *
 * The personaliser (assets/personaliser.js) adds a main line plus add-on lines
 * (e.g. an auto-added chain) that share a `_bundle_id` property. Add-on lines
 * are free — their price is charged on the main line by the Cart Transform — so
 * an add-on must never outlive its parent or drift from its quantity.
 *
 * snippets/cart-products.liquid marks the rows:
 *   data-bundle-id, data-bundle-role="parent|addon", data-quantity
 *
 * Two mechanisms keep them together:
 *   1. Removing / re-quantifying a parent sends its add-ons in the same
 *      /cart/update request (#bundleUpdates).
 *   2. #reconcileBundles runs after every render and repairs anything else
 *      (failed main add, parent dropped by Shopify, stale carts).
 */

/** Only one reconcile request at a time, whichever instance starts it. */
let bundleReconcileInFlight = false;
/** The last repair attempted, so a repair that doesn't stick can't loop. */
let lastBundleReconcileSignature = '';

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
export class CartItemsComponent extends createViewEventElement(Component) {
  #debouncedOnChange = debounce(
    /** @param {Event} event */
    (event) => {
      if (event instanceof QuantitySelectorUpdateEvent) this.#onQuantityChange(event);
    },
    300
  );
  /** @type {Promise<any> | null} */
  #pendingCartFetch = null;

  /**
   * True when the event was dispatched from outside this cart-items-component (e.g.
   * `Shopify.actions.updateCart(...)` from an external app, or the SFAPI default
   * handler). Internal dispatchers (cart-discount-component, cart-note) live inside
   * `this` and either morph the section themselves or don't need a refresh — running
   * a fallback render in that case double-renders and can clobber form state.
   * @param {Event} event
   */
  #isExternalCartUpdate(event) {
    return !(event.target instanceof Node) || !this.contains(event.target);
  }

  /** @param {CartDiscountUpdateEvent} event */
  #handleDiscountUpdate = (event) => {
    const external = this.#isExternalCartUpdate(event);
    event.promise
      ?.then(({ detail }) => {
        const sectionsHtml = detail?.sections?.[this.sectionId];
        if (sectionsHtml) {
          morphSection(this.sectionId, sectionsHtml, { mode: this.isDrawer ? 'hydration' : 'full' });
          this.#updateCartQuantitySelectorButtonStates();
        } else if (external) {
          // External caller (Shopify.actions.updateCart or SFAPI default handler) didn't
          // attach sections; refetch so the discount UI reflects the post-mutation cart.
          // Internal cart-discount-component morphs the section itself — no fallback needed.
          sectionRenderer.renderSection(this.sectionId, {
            cache: false,
            mode: this.isDrawer ? 'hydration' : 'full',
          });
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-items] Event promise rejected:', error);
      });
  };

  /** @param {CartNoteUpdateEvent} event */
  #handleNoteUpdate = (event) => {
    // Internal cart-note dispatches don't need a section refresh — the user typed the
    // value and the textarea retains it. Only external callers need the UI synced.
    if (!this.#isExternalCartUpdate(event)) return;
    event.promise
      ?.then(({ detail }) => {
        const sections = /** @type {Record<string, string> | undefined} */ (detail?.sections);
        const sectionsHtml = sections?.[this.sectionId];
        if (sectionsHtml) {
          morphSection(this.sectionId, sectionsHtml, { mode: this.isDrawer ? 'hydration' : 'full' });
        } else {
          sectionRenderer.renderSection(this.sectionId, {
            cache: false,
            mode: this.isDrawer ? 'hydration' : 'full',
          });
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-items] Event promise rejected:', error);
      });
  };

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    document.addEventListener(StandardEvents.cartDiscountUpdate, this.#handleDiscountUpdate);
    document.addEventListener(StandardEvents.cartNoteUpdate, this.#handleNoteUpdate);

    // Server-rendered cart: repair bundles left inconsistent by a previous page.
    this.#reconcileBundles();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    document.removeEventListener(StandardEvents.cartDiscountUpdate, this.#handleDiscountUpdate);
    document.removeEventListener(StandardEvents.cartNoteUpdate, this.#handleNoteUpdate);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;

    // Cart items require a line number
    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    const lineItemRow = this.refs.cartItemRows[line - 1];

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
      updates: this.#bundleUpdates(lineItemRow, quantity),
    });

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];

    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
      updates: this.#bundleUpdates(cartItemRowToRemove, 0),
    });

    if (!cartItemRowToRemove) return;

    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
      // ...and the personaliser add-on lines that leave the cart with it
      ...this.#bundleAddonRows(cartItemRowToRemove),
    ];

    // If the cart item row is the last row, optimistically trigger the cart empty state
    const isEmptyCart = rowsToRemove.length == this.refs.cartItemRows.length;

    const template = document.getElementById('empty-cart-template');
    if (isEmptyCart && template instanceof HTMLTemplateElement) {
      const clone = document.importNode(template.content, true);

      startViewTransition(() => {
        document.getElementById('cart-drawer-heading')?.remove();
        this.replaceChildren(clone);
      }, [this.isDrawer ? 'empty-cart-drawer' : 'empty-cart-page']);

      return;
    }

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });
  }

  /**
   * The personaliser add-on rows that belong to a parent row.
   * @param {HTMLElement | undefined} row
   * @returns {HTMLElement[]}
   */
  #bundleAddonRows(row) {
    const bundleId = row?.dataset.bundleId;
    if (!bundleId || row?.dataset.bundleRole !== 'parent') return [];

    return (this.refs.cartItemRows ?? []).filter(
      (other) => other.dataset.bundleRole === 'addon' && other.dataset.bundleId === bundleId
    );
  }

  /**
   * The /cart/update payload that moves a parent row and its add-ons to the same
   * quantity in ONE request. Undefined for ordinary lines, which keep using
   * /cart/change.
   * @param {HTMLElement | undefined} row
   * @param {number} quantity
   * @returns {Record<string, number> | undefined}
   */
  #bundleUpdates(row, quantity) {
    const addonRows = this.#bundleAddonRows(row);
    if (!row?.dataset.key || !addonRows.length) return undefined;

    /** @type {Record<string, number>} */
    const updates = { [row.dataset.key]: quantity };
    for (const addonRow of addonRows) {
      if (addonRow.dataset.key) updates[addonRow.dataset.key] = quantity;
    }
    return updates;
  }

  /**
   * Repairs personaliser bundles: removes add-on lines whose parent is gone and
   * brings add-on quantities back in line with their parent. Reads the rendered
   * rows, so it costs nothing when the cart is consistent.
   */
  #reconcileBundles() {
    if (bundleReconcileInFlight) return;

    const rows = /** @type {HTMLElement[]} */ (Array.from(this.querySelectorAll('[data-bundle-role]')));
    if (!rows.length) return;

    /** @type {Map<string, number>} */
    const parentQuantities = new Map();
    for (const row of rows) {
      if (row.dataset.bundleRole === 'parent' && row.dataset.bundleId) {
        parentQuantities.set(row.dataset.bundleId, Number(row.dataset.quantity) || 0);
      }
    }

    /** @type {Record<string, number>} */
    const updates = {};
    for (const row of rows) {
      if (row.dataset.bundleRole !== 'addon' || !row.dataset.key) continue;
      const wanted = parentQuantities.get(row.dataset.bundleId ?? '') ?? 0;
      if ((Number(row.dataset.quantity) || 0) !== wanted) updates[row.dataset.key] = wanted;
    }

    if (!Object.keys(updates).length) {
      lastBundleReconcileSignature = '';
      return;
    }

    // Same repair as last time means it didn't stick (e.g. not enough add-on
    // stock) — leave it rather than hammering the cart.
    const signature = JSON.stringify(updates);
    if (signature === lastBundleReconcileSignature) return;
    lastBundleReconcileSignature = signature;

    bundleReconcileInFlight = true;
    try {
      this.updateQuantity({ action: 'reconcile', updates });
    } catch (error) {
      bundleReconcileInFlight = false;
      console.error('[cart-items] bundle reconcile failed:', error);
    }
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} [config.line] - The line. Omitted by the bundle reconcile, which has no primary line.
   * @param {number} [config.quantity] - The quantity.
   * @param {string} config.action - The action.
   * @param {Record<string, number>} [config.updates] - Line key → quantity. When present the
   *   lines change together through /cart/update instead of /cart/change (personaliser bundles).
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, quantity, updates } = config;
    const { cartTotal } = this.refs;
    const isReconcile = config.action === 'reconcile';

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      ...(updates ? { updates } : { line, quantity }),
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    const deferredUpdatePromise = CartLinesUpdateEvent.createPromise();
    const lineId = line ? this.refs.cartItemRows[line - 1]?.dataset.key ?? '' : '';
    const eventLines = updates
      ? Object.entries(updates).map(([id, lineQuantity]) => ({ id, quantity: lineQuantity }))
      : [{ id: lineId, quantity }];
    const isUpdate = updates
      ? Object.values(updates).some((lineQuantity) => lineQuantity > 0)
      : config.action === 'change' && Number(quantity) > 0;
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: isUpdate ? 'update' : 'remove',
        context: 'cart',
        lines: eventLines,
        promise: deferredUpdatePromise.promise,
      })
    );

    const endpoint = updates ? Theme.routes.cart_update_url : Theme.routes.cart_change_url;

    fetch(`${endpoint}`, fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        // /cart/change reports `errors`; /cart/update reports { status, message, description }.
        const errorMessage =
          parsedResponseText.errors ||
          (parsedResponseText.status ? parsedResponseText.description || parsedResponseText.message : '');

        if (errorMessage) {
          if (line) {
            this.#handleCartError(line, { errors: errorMessage });
          } else {
            // Reconcile has no line to hang an error on: just get back in sync.
            sectionRenderer.renderSection(this.sectionId, {
              cache: false,
              mode: this.isDrawer ? 'hydration' : 'full',
            });
          }
          deferredUpdatePromise.reject(new Error(errorMessage));
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        // Update data-cart-quantity for all matching variants
        this.#updateQuantitySelectors(parsedResponseText);

        deferredUpdatePromise.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(parsedResponseText),
          detail: {
            sections: parsedResponseText.sections,
            items: parsedResponseText.items,
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            didError: false,
          },
        });

        morphSection(this.sectionId, parsedResponseText.sections[this.sectionId], {
          mode: this.isDrawer ? 'hydration' : 'full',
        });

        this.#updateCartQuantitySelectorButtonStates();
      })
      .catch((error) => {
        console.error(error);
        deferredUpdatePromise.reject(error);

        this.dispatchEvent(
          new CartErrorEvent({
            error: error?.message || 'Failed to update cart',
            code: 'SERVICE_UNAVAILABLE',
          })
        );
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);

        if (isReconcile) bundleReconcileInFlight = false;
        // The cart just re-rendered: make sure no bundle came out of it broken.
        this.#reconcileBundles();
      });
  }

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');

    this.dispatchEvent(
      new CartErrorEvent({
        error: parsedResponseText.errors || 'Cart update failed',
        code: 'INVALID',
      })
    );
  };

  /**
   * Handles the cart update.
   *
   * @param {CartLinesUpdateEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event.target === this) return;

    event.promise
      ?.then(async ({ detail }) => {
        const sections = detail?.sections;
        const cartItemsHtml = sections?.[this.sectionId];
        // Animate empty → non-empty in the drawer (possible in squeeze mode
        // where the page is interactive alongside the open drawer). This also
        // needs the response stylesheet because it adds the cart summary markup.
        const wasEmptyCartDrawer = this.isDrawer && this.querySelector('[data-cart-drawer-empty]') !== null;
        /** @type {'hydration' | 'full'} */
        const mode = this.isDrawer ? 'hydration' : 'full';
        const morphOptions = {
          mode,
          injectStylesheet: wasEmptyCartDrawer,
        };

        if (cartItemsHtml) {
          const existingKeys = new Set(this.refs.cartItemRows?.map((row) => row.dataset.key) ?? []);

          if (wasEmptyCartDrawer) {
            startViewTransition(() => {
              morphSection(this.sectionId, cartItemsHtml, morphOptions);
            }, ['fill-cart-drawer']);
          } else {
            await morphSection(this.sectionId, cartItemsHtml, morphOptions);
          }

          // Animate newly added rows (reverse of the remove animation).
          if (!wasEmptyCartDrawer && !prefersReducedMotion()) {
            for (const row of this.refs.cartItemRows ?? []) {
              if (!existingKeys.has(row.dataset.key)) {
                row.classList.add('adding');
                onAnimationEnd(row, () => row.classList.remove('adding'));
              }
            }
          }

          // Update button states for all cart quantity selectors after morph
          this.#updateCartQuantitySelectorButtonStates();
        } else {
          await sectionRenderer.renderSection(this.sectionId, { cache: false, ...morphOptions });
        }

        // e.g. the main item of a personaliser bundle failed to add: its free
        // add-on line is in the cart on its own.
        this.#reconcileBundles();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-items] Event promise rejected:', error);
      });
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  async fetchCartData() {
    if (this.#pendingCartFetch) return this.#pendingCartFetch;

    this.#pendingCartFetch = (async () => {
      const response = await fetch(`${Theme.routes.cart_url}.json`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      });
      if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status} ${response.statusText}`);
      const data = await response.json();
      return data;
    })().finally(() => {
      this.#pendingCartFetch = null;
    });

    return this.#pendingCartFetch;
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }

  /**
   * @returns {boolean} Whether the component is a drawer.
   */
  get isDrawer() {
    return this.dataset.drawer !== undefined;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
